/**
 * Ctrl+Z on the review screen undoes only the person's most recent change, and only while that
 * change's own undo is still on the screen (owner, 2026-10-07: "Ctrl+Z should only ever undo the
 * very last thing"). It invents no undo of its own: for a split or join it presses "Undo boundary
 * change", for a removed page "Put back removed page", for a value changed and saved "Undo change
 * to <field>" (owner, 2026-10-07: type a value, Tab away, Ctrl+Z), and after Confirm & next
 * "✓ Confirmed · Back" while that shows, and after Delete "Deleted an Invoice · Undo" while that shows. When the most
 * recent change has no undo (a value only confirmed, a table row, a type chosen, a page turned, a Confirm that stayed
 * put) or its undo is
 * gone (the Back offer ended, the document changed since), Ctrl+Z does nothing: it never reaches
 * back to an older change. An undo has no redo, so once one is pressed (by Ctrl+Z or by clicking
 * it) a second Ctrl+Z does nothing.
 */
import type { FieldResult } from '../shared/desktop-api';

/** A value changed and saved, and the field as that save left it: the engine takes it back only while the field is still this one. */
export type ValueUndo = {
  jobId: string;
  batchId: string;
  documentId: string;
  fieldName: string;
  field: FieldResult;
};

export type UndoableChange =
  /** Confirm & next moved on and shows this Back offer. */
  | { kind: 'back'; offer: object }
  /** A split or join, by its boundary-queue id. */
  | { kind: 'boundary'; changeId: number }
  /** A removed page, by its entry on the Put back stack. */
  | { kind: 'page'; entry: object }
  /** A value changed and saved, by its "Undo change to <field>" offer. */
  | { kind: 'value'; entry: ValueUndo }
  /** A document deleted, by the Undo on its "Deleted …" notice (a Restore). */
  | { kind: 'delete'; entry: { run: () => void } };

/** A change as noted when the person made it; its undo may be known only once it has saved. */
export type NotedChange = { undo: UndoableChange | null };

export class LastChange {
  private last: NotedChange | null = null;

  /** The person changed something: `undo` is the screen's way to put it back, or null when it has none. */
  note(undo: UndoableChange | null = null): NotedChange {
    this.last = { undo };
    return this.last;
  }

  /** A change noted earlier now has its undo (it saved, or Confirm & next moved on), unless another change came since. */
  settle(change: NotedChange, undo: UndoableChange) {
    if (this.last === change) change.undo = undo;
  }

  /** An undo was pressed: nothing is left for Ctrl+Z to undo. */
  clear() {
    this.last = null;
  }

  /** What Ctrl+Z undoes now: the most recent change, while `offered` says its own undo is still on the screen. */
  undoable(offered: (undo: UndoableChange) => boolean): UndoableChange | null {
    const undo = this.last?.undo ?? null;
    return undo && offered(undo) ? undo : null;
  }
}
